import { useGoBack } from "@/hooks/useGoBack";
import { cn } from "@/lib";
import { LangKey, useTranslation } from "@vixi/localization";
import { ComponentProps } from "react";
import { Button } from "../base/button";

// Constants
const defaultProgressBarWidth = 400;

interface LoadingBarProps extends ComponentProps<"div"> {
  width?: number;
  error?: boolean;
  complete?: boolean;
}

interface ProgressBarProps extends LoadingBarProps {
  /**
   * Floating value from 0 to 100
   */
  progress: number;
  onCompletion?: () => void;
  width?: number;
}

interface LoaderProps {
  isCompleted?: boolean;
  highlightColor?: string;
  highlightWord?: string;
  loadingText?: LangKey;
  completionText?: LangKey;
  width?: number;
  isFailed?: boolean;
  onRetry?: () => void;
}

// TODO: Intoduce some variance / randomness to each iteration of the animation
const LoadingBar = (props: ComponentProps<"div">) => {
  return (
    <div
      {...props}
      className={cn(
        "loading-bar-indeterminate",
        "bg-primary h-full w-1/2 rounded transition-all",
        props.className,
      )}
    />
  );
};

const ProgressBar = ({ progress, ...props }: ProgressBarProps) => {
  return (
    <div
      {...props}
      style={{ width: `${progress}%` }}
      className={cn(
        "loading-bar-determinate",
        "bg-primary h-full rounded transition-all",
        props.className,
      )}
    />
  );
};

const LoaderBar = ({ error, width = 400, complete, ...rootProps }: LoadingBarProps) => {
  return (
    <div
      {...rootProps}
      className={cn(
        "bg-paper flex h-11 items-center justify-center overflow-hidden rounded p-1.5",
        rootProps.className,
      )}
      style={{ width, ...rootProps.style }}
    >
      <div className="relative h-full w-full overflow-hidden rounded">
        {complete ? <ProgressBar progress={100} /> : <LoadingBar />}
      </div>
    </div>
  );
};

const Loader = ({
  isCompleted,
  isFailed,
  onRetry,
  width = defaultProgressBarWidth,
  loadingText = "LoadingMessage.UploadingFile",
  completionText = "SuccessMessage.FileUploaded",
}: LoaderProps) => {
  const { t } = useTranslation();
  useGoBack(null);

  return (
    <div className="w-fit">
      <div>
        <h3 className="lh-44 text-center">{t(isCompleted ? completionText : loadingText)}</h3>
      </div>

      <LoaderBar className="mx-auto" complete={isCompleted} width={width} />

      {isFailed && (
        <Button variant="contained" className="mx-auto mt-3.5 block" onClick={onRetry}>
          {t("Action.TryAgain")}
        </Button>
      )}
    </div>
  );
};

export { Loader, LoaderBar };
export type { LoaderProps };
